/**
 * ledger — Hiệu ứng của một khoản Thu/Chi lên ví, và chính sách chi vượt số dư.
 *
 * Thuần hàm (không Firestore) để test được. Quy ước KHÔNG ĐỔI so với bản cũ,
 * để dữ liệu và lịch sử `transactions` hiện có vẫn đọc đúng:
 * - Thu nhập:           balance += amount            (log type 'income')
 * - Chi, ví thường:     balance −= signed            (log type 'expense')
 * - Chi, thẻ tín dụng:  usedCredit += signed         (log type 'charge' khi tăng, 'payment' khi giảm)
 * với signed = isRefund ? −amount : amount (hoàn tiền đảo chiều).
 * `amount` trong log = độ lệch của trường bị đổi (balance hoặc usedCredit).
 */

export type EntryKind = 'income' | 'expense';

export interface Entry {
  amount?: number;
  isRefund?: boolean;
  walletId?: string | null;
  category?: string;
  description?: string;
}

export interface SourceState {
  name?: string;
  type?: string;
  balance?: number;
  usedCredit?: number;
  creditLimit?: number;
}

export type WalletField = 'balance' | 'usedCredit';

export interface WalletChange {
  walletId: string;
  field: WalletField;
  delta: number;
  txType: 'income' | 'expense' | 'charge' | 'payment';
}

export type Operation = 'add' | 'update' | 'delete';

const num = (v: unknown): number => Number(v) || 0;

/** Tác động của một khoản lên ví của nó; null nếu không gắn ví / số tiền 0. */
export const effectOf = (
  kind: EntryKind,
  entry: Entry | null | undefined,
  isCredit: (walletId: string) => boolean
): { walletId: string; field: WalletField; delta: number } | null => {
  if (!entry || !entry.walletId) return null;
  const amount = num(entry.amount);
  if (!amount) return null;
  if (kind === 'income') return { walletId: entry.walletId, field: 'balance', delta: amount };
  const signed = (entry.isRefund ? -1 : 1) * amount;
  return isCredit(entry.walletId)
    ? { walletId: entry.walletId, field: 'usedCredit', delta: signed }
    : { walletId: entry.walletId, field: 'balance', delta: -signed };
};

export interface Item {
  kind: EntryKind;
  old?: Entry | null;
  next?: Entry | null;
}

/**
 * Gộp tác động của nhiều khoản thành độ lệch THUẬN theo từng (ví, trường):
 * tác động mới trừ tác động cũ. Đổi số tiền cùng ví = một độ lệch; đổi ví =
 * một khoản đảo ví cũ + một khoản ghi ví mới; xoá = đảo ngược hoàn toàn.
 */
export const planChanges = (
  items: Item[],
  isCredit: (walletId: string) => boolean
): WalletChange[] => {
  const acc = new Map<string, WalletChange>();
  const add = (e: ReturnType<typeof effectOf>, sign: 1 | -1, kind: EntryKind) => {
    if (!e) return;
    const key = `${e.walletId}|${e.field}`;
    const cur = acc.get(key) ?? { walletId: e.walletId, field: e.field, delta: 0, txType: kind };
    cur.delta += sign * e.delta;
    acc.set(key, cur);
  };
  for (const it of items) {
    add(effectOf(it.kind, it.old, isCredit), -1, it.kind);
    add(effectOf(it.kind, it.next, isCredit), 1, it.kind);
  }
  return [...acc.values()]
    .filter(c => Math.abs(c.delta) > 1e-9)
    .map(c => ({
      ...c,
      txType: c.field === 'usedCredit' ? (c.delta > 0 ? 'charge' : 'payment') : c.txType
    }));
};

export const vnd = (n: number): string => `${Math.round(n).toLocaleString('vi-VN')} đ`;

export type PolicyResult =
  | { ok: true; next: number; warning?: string }
  | { ok: false; error: string };

/**
 * Chính sách chi vượt (quyết định 24/10/2026):
 * - Ví thường (ngân hàng, tiết kiệm…): CHẶN nếu làm số dư xuống dưới 0
 * - Ví tiền mặt (`cash`): cho phép âm, kèm cảnh báo
 * - Thẻ tín dụng: CHẶN nếu dư nợ vượt hạn mức; dư nợ không xuống dưới 0
 *   (hoàn/đảo quá mức được làm tròn về 0, kèm cảnh báo)
 * Khớp Firestore Rules — chặn ở đây để người dùng thấy thông điệp rõ ràng
 * thay vì bị máy chủ từ chối âm thầm.
 */
export const checkChange = (
  source: SourceState,
  field: WalletField,
  delta: number
): PolicyResult => {
  const name = source.name || 'ví';
  if (field === 'usedCredit') {
    const used = num(source.usedCredit);
    const next = used + delta;
    const limit = source.creditLimit;
    if (delta > 0 && typeof limit === 'number' && next > limit) {
      return {
        ok: false,
        error: `Thẻ "${name}" vượt hạn mức: còn khả dụng ${vnd(Math.max(0, limit - used))}, khoản này cần ${vnd(delta)}.`
      };
    }
    if (next < 0) {
      return { ok: true, next: 0, warning: `Dư nợ thẻ "${name}" được làm tròn về 0 (đảo ${vnd(-next)} vượt dư nợ hiện có).` };
    }
    return { ok: true, next };
  }
  const bal = num(source.balance);
  const next = bal + delta;
  if (next < 0 && delta < 0) {
    if (source.type === 'cash') {
      return { ok: true, next, warning: `Ví tiền mặt "${name}" đang âm ${vnd(-next)} — hãy cập nhật số dư thực tế.` };
    }
    return {
      ok: false,
      error: `Số dư ví "${name}" không đủ: còn ${vnd(bal)}, cần ${vnd(-delta)}. Nạp/chuyển thêm tiền hoặc chọn ví khác.`
    };
  }
  return { ok: true, next };
};
